import { createContext, useContext, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useActiveCategory, ChampionshipCategory } from "@/hooks/useActiveCategory";
import { useActiveStage, ChampionshipStage } from "@/hooks/useActiveStage";

interface PublicChampionshipContextValue {
  championship: any | null;
  championshipId: string | null;
  isLoading: boolean;
  slug: string;
  categories: ChampionshipCategory[];
  activeCategoryId: string | null;
  activeCategory: ChampionshipCategory | null;
  setActiveCategory: (id: string) => void;
  hasMultipleCategories: boolean;
  stages: ChampionshipStage[];
  activeStageId: string | null;
  activeStage: ChampionshipStage | null;
  effectiveStageId: string | null;
  isOverallStage: boolean;
  setActiveStage: (id: string) => void;
  hasMultipleStages: boolean;
}

const PublicChampionshipContext = createContext<PublicChampionshipContextValue>({
  championship: null,
  championshipId: null,
  isLoading: true,
  slug: "",
  categories: [],
  activeCategoryId: null,
  activeCategory: null,
  setActiveCategory: () => {},
  hasMultipleCategories: false,
  stages: [],
  activeStageId: null,
  activeStage: null,
  effectiveStageId: null,
  isOverallStage: false,
  setActiveStage: () => {},
  hasMultipleStages: false,
});

export function usePublicChampionship() {
  return useContext(PublicChampionshipContext);
}

export function PublicChampionshipProvider({ slug, children }: { slug: string; children: ReactNode }) {
  const { data: championship, isLoading } = useQuery({
    queryKey: ["public-championship", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("championships")
        .select("*")
        .eq("public_slug", slug)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!slug,
  });

  const cat = useActiveCategory(championship?.id || null);
  const stg = useActiveStage(championship?.id || null);

  return (
    <PublicChampionshipContext.Provider
      value={{
        championship: championship || null,
        championshipId: championship?.id || null,
        isLoading,
        slug,
        categories: cat.categories,
        activeCategoryId: cat.activeCategoryId,
        activeCategory: cat.activeCategory,
        setActiveCategory: cat.setActiveCategory,
        hasMultipleCategories: cat.hasMultiple,
        stages: stg.stages,
        activeStageId: stg.activeStageId,
        activeStage: stg.activeStage,
        effectiveStageId: stg.effectiveStageId,
        isOverallStage: stg.isOverall,
        setActiveStage: stg.setActiveStage,
        hasMultipleStages: stg.hasMultiple,
      }}
    >
      {children}
    </PublicChampionshipContext.Provider>
  );
}
